'use client';

import { ModeToggle } from '@/components/theme-toggle';
import { Button } from '@/components/ui/button';
import logo from '@/public/logo.png';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import { Menu, MoveRight, X } from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';
import { useModal } from '../modals/useModal';
import Image from 'next/image';

export const Header = () => {
  type NavigationItem = {
    title: string;
    href?: string;
    description: string;
    items?: { title: string; href: string }[];
  };

  const navigationItems: NavigationItem[] = [
    {
      title: 'Home',
      href: '/',
      description: '',
    },
    {
      title: 'Contact',
      href: '#contact',
      description: '',
    },
  ];

  const { openModal } = useModal('login');

  const [isOpen, setOpen] = useState(false);
  return (
    <header className='sticky left-0 top-0 z-40 w-full border-b bg-background'>
      <div className='container relative mx-auto flex min-h-20 flex-row items-center gap-4 lg:grid lg:grid-cols-3'>
        <div className='hidden flex-row items-center justify-start gap-4 lg:flex'>
          <NavigationMenu className='flex items-start justify-start'>
            <NavigationMenuList className='flex flex-row justify-start gap-4'>
              {navigationItems.map((item) => (
                <NavigationMenuItem key={item.title}>
                  {item.href ? (
                    <>
                      <NavigationMenuLink asChild>
                        <Button variant='ghost' asChild>
                          <Link href={item.href}>{item.title}</Link>
                        </Button>
                      </NavigationMenuLink>
                    </>
                  ) : (
                    <>
                      <NavigationMenuTrigger className='text-sm font-medium'>
                        {item.title}
                      </NavigationMenuTrigger>
                      <NavigationMenuContent className='!w-[450px] p-4'>
                        <div className='flex grid-cols-2 flex-col gap-4 lg:grid'>
                          <div className='flex h-full flex-col justify-between'>
                            <div className='flex flex-col'>
                              <p className='text-base'>{item.title}</p>
                              <p className='text-sm text-muted-foreground'>
                                {item.description}
                              </p>
                            </div>
                            <Button size='sm' className='mt-10' asChild>
                              <Link href='/contact'>Book a call today</Link>
                            </Button>
                          </div>
                          <div className='flex h-full flex-col justify-end text-sm'>
                            {item?.items?.map((subItem) => (
                              <NavigationMenuLink
                                href={subItem.href}
                                key={subItem.title}
                                className='flex flex-row items-center justify-between rounded px-4 py-2 hover:bg-muted'
                              >
                                <span>{subItem.title}</span>
                                <MoveRight className='h-4 w-4 text-muted-foreground' />
                              </NavigationMenuLink>
                            ))}
                          </div>
                        </div>
                      </NavigationMenuContent>
                    </>
                  )}
                </NavigationMenuItem>
              ))}
            </NavigationMenuList>
          </NavigationMenu>
        </div>
        <div className='flex items-center gap-2 lg:justify-center'>
          <Image
            src={logo}
            alt='Logo'
            width={400}
            height={200}
            className='w-full object-contain sm:w-48'
          />
        </div>
        <div className='flex w-full justify-end gap-4'>
          <Button variant='ghost' className='hidden md:inline' asChild>
            <Link href='#contact'>Contact us</Link>
          </Button>
          <div className='hidden border-r md:inline' />
          <ModeToggle />
          <Button variant='outline' onClick={openModal}>
            Sign in
          </Button>
        </div>
        <div className='flex w-12 shrink items-end justify-end lg:hidden'>
          <Button variant='ghost' onClick={() => setOpen(!isOpen)}>
            {isOpen ? <X className='h-5 w-5' /> : <Menu className='h-5 w-5' />}
          </Button>
          {isOpen && (
            <div className='container absolute right-0 top-20 flex w-full flex-col gap-8 border-t bg-background py-4 shadow-lg'>
              {navigationItems.map((item) => (
                <div key={item.title}>
                  <div className='flex flex-col gap-2'>
                    {item.href ? (
                      <Link
                        href={item.href}
                        className='flex items-center justify-between'
                        target={
                          item.href.startsWith('http') ? '_blank' : undefined
                        }
                        rel={
                          item.href.startsWith('http')
                            ? 'noopener noreferrer'
                            : undefined
                        }
                      >
                        <span className='text-lg'>{item.title}</span>
                        <MoveRight className='h-4 w-4 stroke-1 text-muted-foreground' />
                      </Link>
                    ) : (
                      <p className='text-lg'>{item.title}</p>
                    )}
                    {item.items?.map((subItem) => (
                      <Link
                        key={subItem.title}
                        href={subItem.href}
                        className='flex items-center justify-between'
                      >
                        <span className='text-muted-foreground'>
                          {subItem.title}
                        </span>
                        <MoveRight className='h-4 w-4 stroke-1' />
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
